import React, { useState, useMemo, useEffect } from 'react';
import Modal from 'react-modal';
import { ModalClose } from '../icon/ModalClose';
import {
  useWalletSelector,
  Ipermission,
} from '../../context/WalletSelectorContext';
import { toReadableNumber, toPrecision } from '../../utils/numbers';
import { batchDeleteKeys } from '../../services/portfolioData';
import { getPublicKey } from '../../services/walletKeys';

const maxLength = 10;

function AccessKeyModal(props: {
  isOpen: boolean;
  onRequestClose: () => void;
}) {
  const { isOpen, onRequestClose } = props;
  const [currentUsedKeys, setCurrentUsedKeys] = useState<string[]>([]);
  const { accountId } = useWalletSelector();
  useEffect(() => {
    getUsedKeys();
  }, [accountId]);
  async function getUsedKeys() {
    const keyUsed = await getPublicKey(accountId);
    setCurrentUsedKeys([keyUsed]);
  }
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          zIndex: 80,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        content: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
          background: 'transparent',
          border: 'none',
          padding: 0,
          overflow: 'visible',
        },
      }}
    >
      <div
        style={{
          width: 460,
          maxWidth: '92vw',
          background: 'rgba(14, 20, 38, 0.86)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: '22px 22px 18px',
        }}
      >
        <div className="flex items-start justify-between">
          <div className="pr-6">
            <div className="text-white text-lg font-medium">Approved Keys</div>
            <div
              className="text-sm mt-1"
              style={{ color: 'rgba(231, 238, 242, 0.62)' }}
            >
              One gas key per app. Remove extras here.
            </div>
          </div>
          <ModalClose
            className="cursor-pointer mt-1"
            onClick={onRequestClose}
          />
        </div>
        <AuthorizedApps currentUsedKeys={currentUsedKeys} />
      </div>
    </Modal>
  );
}

function AuthorizedApps({ currentUsedKeys }: { currentUsedKeys: string[] }) {
  const [clear_loading, set_clear_loading] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set([]));
  const { allKeys } = useWalletSelector();
  const disabledWallet = ['sender', 'neth', 'keypom', 'okx-wallet'];
  const selectedWalletId = window.selector?.store?.getState()?.selectedWalletId;
  const functionCallKeys = useMemo(() => {
    return allKeys.filter((key) => key.access_key.permission !== 'FullAccess');
  }, [allKeys]);
  const allCheckedLength = useMemo(() => {
    if (functionCallKeys?.length) {
      const unusedKeys = functionCallKeys.filter(
        (accessKey) => !currentUsedKeys.includes(accessKey.public_key)
      );
      return Math.min(unusedKeys.length, maxLength);
    }
    return 0;
  }, [functionCallKeys, currentUsedKeys.length]);
  function getAllowance(allowance: string) {
    if (allowance) {
      return toPrecision(toReadableNumber(24, allowance), maxLength) + ' NEAR';
    }
    return 'Unlimited';
  }
  function onCheck(public_key) {
    if (selectedKeys.has(public_key)) {
      selectedKeys.delete(public_key);
    } else {
      if (selectedKeys.size == maxLength) return;
      selectedKeys.add(public_key);
    }
    setSelectedKeys(new Set(selectedKeys));
  }
  function onCheckAll() {
    if (selectedKeys.size == allCheckedLength) {
      setSelectedKeys(new Set([]));
    } else {
      const checkedAll = functionCallKeys
        .filter((key) => !currentUsedKeys.includes(key.public_key))
        .slice(0, maxLength)
        .reduce((publicKeys, accessKey) => {
          publicKeys.push(accessKey.public_key);
          return publicKeys;
        }, []);
      setSelectedKeys(new Set(checkedAll));
    }
  }
  function batchClear() {
    set_clear_loading(true);
    batchDeleteKeys(Array.from(selectedKeys));
  }
  function switchWallet() {
    window.modal.show();
  }
  const disabled = selectedKeys.size === 0;
  const isEmpty = functionCallKeys.length == 0;
  const isDisabledAction = disabledWallet.includes(selectedWalletId);
  return (
    <div className="pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-white font-medium">Keys</span>
          <span
            className="text-xs px-2 py-0.5"
            style={{
              color: '#04121A',
              background: '#00DAFF',
              borderRadius: 999,
            }}
          >
            {functionCallKeys.length}
          </span>
        </div>
        {allCheckedLength > 0 && !isDisabledAction ? (
          <div
            className="flex items-center gap-1.5 text-sm"
            style={{ color: 'rgba(231, 238, 242, 0.72)' }}
          >
            <Checkbox
              onClick={onCheckAll}
              checked={selectedKeys.size == allCheckedLength}
            />
            Remove 10
          </div>
        ) : null}
      </div>
      <div className="overflow-auto" style={{ maxHeight: 320 }}>
        {functionCallKeys.map((accessKey) => {
          const isUsed = currentUsedKeys.includes(accessKey.public_key);
          return (
            <div
              key={accessKey.public_key}
              className="mb-3"
              style={{
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: '14px 16px',
              }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-medium text-white break-all">
                    {
                      (accessKey?.access_key?.permission as Ipermission)
                        ?.FunctionCall?.receiver_id
                    }
                  </p>
                  {isUsed ? (
                    <span
                      className="text-xs whitespace-nowrap px-2 py-0.5"
                      style={{
                        color: '#00DAFF',
                        background: 'rgba(0, 218, 255, 0.12)',
                        borderRadius: 999,
                      }}
                    >
                      In use
                    </span>
                  ) : null}
                </div>
                {!isUsed && (
                  <Checkbox
                    checked={selectedKeys.has(accessKey.public_key)}
                    hidden={isDisabledAction}
                    onClick={() => {
                      onCheck(accessKey.public_key);
                    }}
                  />
                )}
              </div>
              <div
                className="text-xs my-3 break-all"
                style={{
                  background: '#0b1020',
                  color: 'rgba(231, 238, 242, 0.72)',
                  borderRadius: 10,
                  padding: '8px 10px',
                }}
              >
                {accessKey.public_key}
              </div>
              <div className="flex items-center text-sm gap-1.5">
                <span style={{ color: 'rgba(231, 238, 242, 0.62)' }}>
                  Fee allowance
                </span>
                <span className="text-white">
                  {getAllowance(
                    (accessKey?.access_key?.permission as Ipermission)
                      ?.FunctionCall?.allowance
                  )}
                </span>
              </div>
            </div>
          );
        })}
        {isEmpty ? (
          <div
            className="flex justify-center text-sm"
            style={{
              color: 'rgba(231, 238, 242, 0.5)',
              padding: '48px 0 36px',
            }}
          >
            No approved keys
          </div>
        ) : null}
      </div>
      {isDisabledAction ? (
        <div
          className="text-sm mt-4"
          style={{
            color: '#E7EEF2',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '10px 14px',
          }}
        >
          This wallet cannot delete keys. Switch wallets to remove them.
        </div>
      ) : null}
      {!isDisabledAction ? (
        <button
          type="button"
          disabled={disabled || clear_loading}
          onClick={batchClear}
          className="w-full mt-4 text-sm font-medium"
          style={{
            height: 44,
            borderRadius: 999,
            background: '#00DAFF',
            color: '#04121A',
            opacity: disabled || clear_loading ? 0.4 : 1,
          }}
        >
          {clear_loading ? 'Clearing…' : 'Clear'}
        </button>
      ) : (
        <button
          type="button"
          onClick={switchWallet}
          className="w-full mt-4 text-sm font-medium"
          style={{
            height: 44,
            borderRadius: 999,
            background: 'transparent',
            color: '#00DAFF',
            border: '1px solid #00DAFF',
          }}
        >
          Switch wallet
        </button>
      )}
    </div>
  );
}

function Checkbox({
  checked,
  onClick,
  hidden,
}: {
  checked?: boolean;
  onClick: () => void;
  hidden?: boolean;
}) {
  if (hidden) return null;
  if (checked) {
    return (
      <div
        onClick={onClick}
        className="flex items-center justify-center cursor-pointer w-4 h-4 flex-shrink-0"
        style={{ background: '#00DAFF', borderRadius: 4 }}
      >
        <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
          <path
            d="M1.2666 4.00016L4.11105 6.66683L9.79994 1.3335"
            stroke="#04121A"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }
  return (
    <span
      onClick={onClick}
      className="inline-block w-4 h-4 cursor-pointer flex-shrink-0"
      style={{
        borderRadius: 4,
        border: '1px solid rgba(255,255,255,0.28)',
        background: '#0b1020',
      }}
    ></span>
  );
}

export default AccessKeyModal;
